/**
 * Pindah ke keuskupan lain (owner QoL 7, 2026-10-08; schema 13): a family, a person or a Pelayan
 * moves to a diocese outside SATU, which has no receiving parish here, so no mutasi records it
 * (a move inside SATU stays a mutasi, unchanged).
 *
 * - A family: its status becomes `pindah` (the existing status, so the Keluarga list, the
 *   letters' search and every report that reads `status = 'aktif'` already leave it out), with
 *   the destination diocese, its parish when known (both as typed) and the date; each living
 *   member gets the same three columns, so every person count leaves them out with one test
 *   (`belumPindahSql`). A person alone: the three columns on the person, the household link kept.
 *   "Batalkan pindah" clears them: the family is `aktif` again, and the members that moved with
 *   it (the same diocese and date) with it; one moved on their own before stays moved.
 * - A Pelayan: every current assignment ends on the date, as Nonaktifkan orang ends them (an
 *   account tied to one is switched off by the server as today), the person is inactive (`aktif`
 *   false) with the diocese and the date. "Aktifkan kembali" makes them active again; ended
 *   assignments stay ended. A Pelayan reads as moved only while inactive: an older path that
 *   reactivates them (Ubah, a new assignment) leaves the columns as history.
 *
 * Who (the push enforces it, `periksaPindahKeuskupan`): an admin paroki for its own parish's
 * families and people (the admin keuskupan acts as one, plan 10), the admin keuskupan alone for a
 * Pelayan; staff and the ketua roles may not, and staff may not reactivate a moved family by its
 * status either. Pure.
 */
import { parsePartialDateInput, partialDateEarliest } from './dates/partial'
import { namaParokiLengkap } from './namaParoki'
import type { Peran } from './schema/contract'
import { format, uiStrings } from './strings/index'
import type { LocalWrite, Op, Row } from './sync/ops'
import type { Actor } from './sync/visibility'

const t = uiStrings.keluarga.pindahKeuskupan

/** A family's and a person's columns of the move. */
export const KOLOM_PINDAH_KEUSKUPAN = [
  'pindah_keuskupan',
  'pindah_paroki',
  'pindah_tanggal',
] as const
/** A Pelayan's (no parish: their next post is the other diocese's to give). */
export const KOLOM_PINDAH_PELAYAN = ['pindah_keuskupan', 'pindah_tanggal'] as const

/** A move as recorded: the diocese and the parish as typed, the date (partial, canonical). */
export interface PindahKeuskupan {
  keuskupan: string
  paroki: string | null
  tanggal: string
}

const isi = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() !== '' ? v.trim() : null

/** The move a stored row records, or null when it records none. */
export function pindahDari(row: Readonly<Record<string, unknown>>): PindahKeuskupan | null {
  const keuskupan = isi(row.pindah_keuskupan)
  if (keuskupan === null) return null
  return { keuskupan, paroki: isi(row.pindah_paroki), tanggal: isi(row.pindah_tanggal) ?? '' }
}

/** "Person still here": the SQL a list, a count or a report adds over an umat alias. */
export function belumPindahSql(alias: string): string {
  return `${alias}.pindah_keuskupan IS NULL`
}

export type HasilIsianPindah =
  | { ok: true; nilai: PindahKeuskupan }
  | { ok: false; galat: Partial<Record<'keuskupan' | 'tanggal', string>> }

/**
 * The dialog's fields as typed: the diocese and the date are needed, the parish may stay empty;
 * the date in any of SATU's partial forms ("05-10-2026", "10-2026", "2026"), not after today
 * (`hariIni`, canonical, WIT).
 */
export function bacaIsianPindah(
  isian: { keuskupan: string; paroki: string; tanggal: string },
  hariIni: string,
): HasilIsianPindah {
  const galat: Partial<Record<'keuskupan' | 'tanggal', string>> = {}
  const keuskupan = isi(isian.keuskupan)
  if (keuskupan === null) galat.keuskupan = t.keuskupanWajib
  const ketik = isian.tanggal.trim()
  const tanggal = ketik === '' ? null : parsePartialDateInput(ketik)
  if (ketik === '') galat.tanggal = uiStrings.validasi.wajib
  else if (tanggal === null) galat.tanggal = uiStrings.validasi.tanggalParsial
  else if (hariIni !== '' && partialDateEarliest(tanggal) > hariIni) galat.tanggal = t.tanggalNanti
  if (keuskupan === null || tanggal === null || Object.keys(galat).length > 0) {
    return { ok: false, galat }
  }
  return { ok: true, nilai: { keuskupan, paroki: isi(isian.paroki), tanggal } }
}

const kolom = (p: PindahKeuskupan | null) => ({
  pindah_keuskupan: p?.keuskupan ?? null,
  pindah_paroki: p?.paroki ?? null,
  pindah_tanggal: p?.tanggal ?? null,
})

/** A family's move: its status and destination, then each living member's (`anggotaIds`). */
export function tulisanPindahKeluarga(o: {
  keluargaId: string
  anggotaIds: readonly string[]
  pindah: PindahKeuskupan
}): LocalWrite[] {
  return [
    { table: 'keluarga', row_id: o.keluargaId, fields: { status: 'pindah', ...kolom(o.pindah) } },
    ...o.anggotaIds.map((id) => tulisanPindahUmat(id, o.pindah)),
  ]
}

const samaPindah = (a: PindahKeuskupan | null, b: PindahKeuskupan | null): boolean =>
  a !== null &&
  b !== null &&
  a.keuskupan === b.keuskupan &&
  a.tanggal === b.tanggal &&
  (a.paroki ?? '') === (b.paroki ?? '')

/** "Batalkan pindah" of a family: active again, with the members that moved with it. */
export function tulisanBatalPindahKeluarga(o: {
  keluarga: { id: string; pindah: PindahKeuskupan | null }
  anggota: readonly { id: string; pindah: PindahKeuskupan | null }[]
}): LocalWrite[] {
  return [
    { table: 'keluarga', row_id: o.keluarga.id, fields: { status: 'aktif', ...kolom(null) } },
    ...o.anggota
      .filter((a) => samaPindah(a.pindah, o.keluarga.pindah))
      .map((a) => tulisanBatalPindahUmat(a.id)),
  ]
}

export function tulisanPindahUmat(umatId: string, pindah: PindahKeuskupan): LocalWrite {
  return { table: 'umat', row_id: umatId, fields: kolom(pindah) }
}

export function tulisanBatalPindahUmat(umatId: string): LocalWrite {
  return { table: 'umat', row_id: umatId, fields: kolom(null) }
}

/**
 * A Pelayan's move: the person inactive with the diocese and the date, then the end of each
 * current assignment it ends (`tugasIds`, as Nonaktifkan orang picks them) on that date.
 */
export function tulisanPindahPelayan(o: {
  pelayanId: string
  tugasIds: readonly string[]
  keuskupan: string
  tanggal: string
}): LocalWrite[] {
  return [
    {
      table: 'pelayan',
      row_id: o.pelayanId,
      fields: { aktif: false, pindah_keuskupan: o.keuskupan, pindah_tanggal: o.tanggal },
    },
    ...o.tugasIds.map(
      (id): LocalWrite => ({ table: 'penugasan', row_id: id, fields: { sampai: o.tanggal } }),
    ),
  ]
}

/** "Aktifkan kembali" of a Pelayan who moved: active, the move cleared. */
export function tulisanAktifkanPelayan(pelayanId: string): LocalWrite {
  return {
    table: 'pelayan',
    row_id: pelayanId,
    fields: { aktif: true, pindah_keuskupan: null, pindah_tanggal: null },
  }
}

/** The roles that record (and reverse) a family's or a person's move. */
export function bolehPindahKeuskupan(peran: Peran | null | undefined): boolean {
  return peran === 'admin_paroki' || peran === 'admin_keuskupan'
}

/** The role that records (and reverses) a Pelayan's move. */
export function bolehPindahPelayan(peran: Peran | null | undefined): boolean {
  return peran === 'admin_keuskupan'
}

/** "Keuskupan " before a name that does not start with it ("Keuskupan Agung …" stays). */
export function namaKeuskupanLengkap(nama: string): string {
  const n = nama.trim()
  if (n === '' || /^keuskupan(\s|$)/i.test(n)) return n
  return `Keuskupan ${n}`
}

/** "Paroki St. Stefanus Sempan, Keuskupan Timika", or the diocese alone. */
export function tujuanPindahKeuskupan(p: Pick<PindahKeuskupan, 'keuskupan' | 'paroki'>): string {
  const keuskupan = namaKeuskupanLengkap(p.keuskupan)
  return p.paroki === null ? keuskupan : `${namaParokiLengkap(p.paroki)}, ${keuskupan}`
}

/** "Pindah ke Keuskupan Timika sejak 5 Oktober 2026…": a band's sentence. */
export function kalimatPindah(templat: string, p: PindahKeuskupan, tanggal: string): string {
  return format(templat, { tujuan: tujuanPindahKeuskupan(p), tanggal })
}

export type PeriksaPindah = { ok: true } | { ok: false; detail: string }

/**
 * The push rule for one op (spec 9.5 step 4b): an op that sets or clears a move on a family or a
 * person, or changes the status of a family that moved to another diocese, is an admin paroki's
 * (`actor` as `pelakuPush` made it: the admin keuskupan writes a parish's records as its admin
 * paroki); one that sets or clears a Pelayan's move is the admin keuskupan's. Every other op
 * passes, so an op from an app that does not know these columns passes as before.
 */
export function periksaPindahKeuskupan(actor: Actor, op: Op, current: Row | null): PeriksaPindah {
  const fields = op.fields
  if (op.table === 'keluarga' || op.table === 'umat') {
    const menyentuh = KOLOM_PINDAH_KEUSKUPAN.some((k) => k in fields)
    const statusPindahan =
      op.table === 'keluarga' &&
      'status' in fields &&
      current !== null &&
      isi(current.pindah_keuskupan) !== null
    if ((menyentuh || statusPindahan) && actor.peran !== 'admin_paroki') {
      return { ok: false, detail: t.bukanAdmin }
    }
    return { ok: true }
  }
  if (op.table === 'pelayan') {
    if (KOLOM_PINDAH_PELAYAN.some((k) => k in fields) && actor.peran !== 'admin_keuskupan') {
      return { ok: false, detail: t.bukanAdminKeuskupan }
    }
  }
  return { ok: true }
}
